import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { canon } from '../../../seo';
import SocialMetaTags from '../../../components/SocialMetaTags';
import { Box } from '@mui/material';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import LandingHeader from '../../../layout/header';
import Footer from '../../../layout/footer';
import styles from './SearchPapersDetail.module.css';
import Login from '../../authentication/login';
import Signup from '../../authentication/sign up';
import Contact from '../../subscription/Contact';
import ForgotPassword from '../../Admin/ForgotPassword';
import OtherFeaturesSection from './OtherFeaturesSection';

import seeHowItWorksIcon from '../../../assets/new_design/seehowitwork.png';
import addCircleWhiteIcon from '../../../assets/new_design/add_circle_white.png';
import learnMoreIcon from '../../../assets/new_design/learn-more-icon.png';
import PaperSearch2Demo from '../../../components/papersearch2demo';

// Section 2 Icons
import search0Icon from '../../../assets/new_design/search0.png';
import search1Icon from '../../../assets/new_design/search1.png';
import search2Icon from '../../../assets/new_design/search2.png';
import search3Icon from '../../../assets/new_design/search3.png';
import search4Icon from '../../../assets/new_design/search4.png';
import searchFilterIcon from '../../../assets/new_design/searchfilter.png';
import searchFilter2Icon from '../../../assets/new_design/searchfilter2.webp';
import searchTabIcon from '../../../assets/new_design/searchtab.webp';
import searchCitationTabIcon from '../../../assets/new_design/searchcitationtab.webp';
import searchLibraryIcon from '../../../assets/new_design/searchlibrary.webp';
import libraryTabIcon from '../../../assets/new_design/librarytab.webp';
import thumbIcon from '../../../assets/new_design/thumb.png';

const META = {
  title: 'AI Academic Paper Search & Discovery | ResearchPal',
  description: 'Find relevant papers with natural-language search, academic filters, open-access options, citation trails, and connected research discovery.',
};


const SearchPapersDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState({
    loginModal: location.state ? true : false,
    forgotModal: false,
    signupModal: false,
    contactUsModal: false,
  });

  const toggle = (name) => {
    if (name === "forgotModal" || name === "signupModal") {
      setState((prev) => ({ ...prev, [name]: !prev[name], loginModal: false }));
    } else {
      setState((prev) => ({ ...prev, [name]: !prev[name] }));
    }
  };

  // "Get Started" CTAs: no raw /pricing link. Logged-in → pricing; otherwise → login popup.
  const handleGetStarted = () => {
    if (localStorage.getItem("token")) {
      navigate("/pricing");
    } else {
      toggle("loginModal");
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box sx={{
      backgroundColor: '#F6F7F9',
      minHeight: '100vh',
      maxWidth: '1600px',
      margin: '0 auto',
      pt: { xs: "64px", md: "86px" },
      position: 'relative',
      overflow: 'hidden'
    }}>
      <Helmet>
        <title>{META.title}</title>
        <meta
          name="description"
          content={META.description}
        />
        <link rel="canonical" href={canon('/features/search-papers')} />
      </Helmet>
      <SocialMetaTags path="/features/search-papers" title={META.title} description={META.description} image="search-papers" />
      <Box
        sx={{
          zIndex: 1100,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: { xs: '64px', md: '86px' },
          backgroundColor: '#F6F7F9',
          borderBottom: '1px solid rgba(0,0,0,0.05)',
        }}
      >
        <LandingHeader onClick={(e) => toggle(e)} />
      </Box>

      {/* Main Container */}
      <div className={styles.container}>

        {/* Title */}
        <h1 className={styles.heroTitle}>
          Search Academic Papers Smarter with AI-Powered Discovery
        </h1>

        {/* Subtitle */}
        <p className={styles.heroSubtitle}>
          Find relevant research papers faster using intelligent search, advanced filters, and structured paper insights. Explore citations, references, and open-access PDFs in one research workflow.
        </p>

        {/* Buttons */}
        <div className={styles.buttonGroup}>
          <Link to="/demo" state={{ demoTab: 'Search Papers' }} className={styles.buttonSeeHow} style={{ textDecoration: 'none', color: 'inherit' }}>
            <img src={seeHowItWorksIcon} alt="See How It Works" className={styles.buttonIcon} />
            See How It Works
          </Link>

          <div
            className={styles.buttonGetStarted}
            style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onClick={handleGetStarted}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleGetStarted(); } }}
          >
            <img src={learnMoreIcon} alt="Get Started" className={styles.buttonIcon} />
            Get Started
          </div>
        </div>

        {/* Subtext */}
        <p className={styles.footerSubtext}>
          No setup, no hassle — Free plan available — No credit card required.
        </p>

        {/* Search Papers Demo Animation */}
        <div className={styles.imageGroup}>
          <PaperSearch2Demo />
        </div>

        {/* SECTION 2 */}
        <div className={styles.sec2}>
          <div className={styles.sec2Title}>
            Enter Your Research Topic and Start Exploring
          </div>

          <img
            src={search0Icon}
            alt="Search Bar Interface"
            className={styles.screenshot591}
          />

          <div className={styles.sec2Label}>
            ResearchPal's Search Papers feature helps you quickly discover relevant academic research based on your topic or research question. Simply enter your query and ResearchPal searches across academic sources to surface the most relevant research papers.<br /><br />You can also enable Open Access mode to prioritize papers with available PDFs or publisher-accessible versions, helping you access research content faster.
          </div>

          <div className={styles.rectangleParent}>
            {/* Card 1 */}
            <div className={styles.featureCard}>
              <img src={search1Icon} alt="Input Icon" className={styles.featureIcon} />
              <div className={styles.featureText}>Enter research topic or question</div>
            </div>

            {/* Card 2 */}
            <div className={styles.featureCard}>
              <img src={search2Icon} alt="Dictionary Icon" className={styles.featureIcon} />
              <div className={styles.featureText}>Open Access toggle for full papers</div>
            </div>

            {/* Card 3 */}
            <div className={styles.featureCard}>
              <img src={search3Icon} alt="Stack Icon" className={styles.featureIcon} />
              <div className={styles.featureText}>Fast academic search results</div>
            </div>

            {/* Card 4 */}
            <div className={styles.featureCard}>
              <img src={search4Icon} alt="List Icon" className={styles.featureIcon} />
              <div className={styles.featureText}>Clean structured results</div>
            </div>
          </div>
        </div>

        {/* SECTION 3 */}
        <div className={styles.sec3Wrapper}>
          <div className={styles.sec3Left}>
            <div className={styles.sec3Title}>
              Refine Results with Advanced Academic Filters
            </div>
            <div className={styles.sec3Label}>
              <p>
                ResearchPal provides advanced filtering options to help you narrow down results and find the most relevant academic papers. Filter research results based on domain, publication type, and date range to quickly identify the most suitable sources for your research.
              </p>
              <p className={styles.filtersAvailable}>Filters Available:</p>
              <ul className={styles.sec3List}>
                <li>Domain filtering</li>
                <li>Publication type filtering</li>
                <li>Date range selection</li>
                <li>Open access filtering</li>
                <li>Research topic relevance</li>
              </ul>
              <p>
                This helps researchers avoid irrelevant results and focus only on high-quality academic sources.
              </p>
            </div>
          </div>

          <img
            src={searchFilterIcon}
            alt="Search Filters Interface"
            className={styles.screenshot651}
          />
        </div>

        {/* SECTION 4 */}
        <div className={styles.sec4Wrapper}>
          <div className={styles.sec4Left}>
            <div className={styles.sec4Title}>
              Refine Results with<br />Advanced Academic Filters
            </div>
            <img
              src={searchFilter2Icon}
              alt="Search Results Details"
              className={styles.screenshot602}
            />
          </div>

          <div className={styles.sec4Right}>
            <div className={styles.sec4Label}>
              <p>
                Each research result displays key information to help you evaluate relevance quickly, including paper title, abstract preview, authors, citations, references, and publication details. This structured view allows researchers to quickly scan and shortlist relevant papers.
              </p>
              <p className={styles.eachResultIncludes}>Each Result Includes:</p>
              <ul className={styles.sec4List}>
                <li>Paper title</li>
                <li>Authors & publication year</li>
                <li>Citation count</li>
                <li>Reference count</li>
                <li>Abstract preview</li>
                <li>DOI & source links</li>
                <li>Save & open options</li>
              </ul>
              <p>
                This reduces time spent opening multiple tabs and scanning papers manually.
              </p>
            </div>
          </div>
        </div>

        {/* SECTION 5 */}
        <div className={styles.sec5Wrapper}>
          <h2 className={styles.sec5Title}>
            Understand Papers Faster with Smart Summaries,<br />Abstracts & Complete Paper Text
          </h2>

          <img
            src={searchTabIcon}
            alt="Search Tab Details"
            className={styles.screenshot612}
          />

          <div className={styles.sec5Description}>
            When you open a research paper, ResearchPal displays a structured detail view with smart summaries and abstracts when available. This helps you quickly understand the paper's purpose, methodology, and findings without reading the full document immediately. When available, ResearchPal will display full paper text if the open-access toggle is enabled.
          </div>

          {/* Row 1 */}
          <div className={styles.frameParent}>
            <div className={styles.labelWrapper}>
              <div className={styles.label3}>Smart Summary</div>
            </div>
            <div className={styles.labelContainer}>
              <div className={styles.label3}>Abstract</div>
            </div>
            <div className={styles.labelFrame}>
              <div className={styles.label3}>Citation Count</div>
            </div>
            <div className={styles.frameDiv}>
              <div className={styles.label3}>Reference Count</div>
            </div>
          </div>

          {/* Row 2 */}
          <div className={styles.frameGroup}>
            <div className={styles.labelWrapper}>
              <div className={styles.label3}>Publisher Link</div>
            </div>
            <div className={styles.labelContainer}>
              <div className={styles.label3}>PDF Link</div>
            </div>
            <div className={styles.labelFrame}>
              <div className={styles.label3}>Source Link</div>
            </div>
            <div className={styles.frameDiv}>
              <div className={styles.label3}>Save Options</div>
            </div>
          </div>

          <div className={styles.sec5Footer}>
            This allows researchers to evaluate papers faster and make better research decisions.
          </div>
        </div>

        {/* SECTION 6 */}
        <div className={styles.sec6Wrapper}>
          <h2 className={styles.sec6Title}>
            Navigate Related Research with Nested Citations
          </h2>

          <img
            src={searchCitationTabIcon}
            alt="Nested Citations Navigation"
            className={styles.screenshot642}
          />

          <div className={styles.sec6Description}>
            ResearchPal allows you to explore citations and references directly within the paper detail view. These nested papers open in the same panel, enabling seamless research exploration without losing context.
          </div>

          <div className={styles.sec6Grid}>
            <div className={styles.sec6Card}>
              <div className={styles.sec6CardText}>Explore cited papers</div>
            </div>
            <div className={styles.sec6Card}>
              <div className={styles.sec6CardText}>Explore referenced<br />papers</div>
            </div>
            <div className={styles.sec6Card}>
              <div className={styles.sec6CardText}>Navigate between<br />related research</div>
            </div>
            <div className={styles.sec6Card}>
              <div className={styles.sec6CardText}>Return to previous<br />paper easily</div>
            </div>
          </div>

          <div className={styles.sec6Footer}>
            This creates a connected research discovery workflow similar to academic citation networks.
          </div>
        </div>

        {/* SECTION 7 */}
        <div className={styles.sec7Wrapper}>
          <img
            src={searchLibraryIcon}
            alt="Search Library"
            className={styles.screenshot661}
          />
          <div className={styles.sec7Right}>
            <div className={styles.sec7Title}>
              Save Papers and Build Your<br />Research Library
            </div>
            <div className={styles.sec7Label}>
              <p>With one click, you can save research papers directly to your Research Library. ResearchPal automatically stores citations, abstracts, and available PDFs to help you build a structured research collection.</p>
              <p className={styles.ifOpenAccess}>If Open Access PDF Available:</p>
              <ul className={styles.sec7List}>
                <li>Save citation</li>
                <li>Save abstract</li>
                <li>Save full PDF</li>
              </ul>
              <p className={styles.ifOpenAccess}>If PDF Not Available:</p>
              <ul className={styles.sec7List}>
                <li>Save citation</li>
                <li>Save abstract metadata</li>
              </ul>
              <p>This ensures your research stays organized and accessible.</p>
            </div>
          </div>
        </div>

        {/* SECTION 8 */}
        <div className={styles.sec8Wrapper}>
          <div className={styles.sec8Left}>
            <div className={styles.sec8Title}>
              Seamlessly Connect Search with Your Research Workflow
            </div>
            <div className={styles.sec8Label}>
              Saved papers automatically integrate with your ResearchPal workflow, making them available across your projects. Citations are stored in the citation manager, abstracts are saved in the research library, and PDFs are accessible whenever needed.<br /><br />This creates a unified research workflow from discovery to writing.
            </div>
          </div>
          <img
            src={libraryTabIcon}
            alt="Library Connection"
            className={styles.screenshot662}
          />
        </div>

        {/* AI RESEARCH ASSISTANT SECTION */}
        <div className={styles.aiAgentSection}>
          <div className={styles.aiAgentBadge}>
            <span>AI Research Assistant</span>
          </div>

          <h2 className={styles.aiAgentTitle}>
            From Finding Papers to Understanding Research With an AI Research Assistant
          </h2>

          <div className={styles.aiAgentIntro}>
            <p className={styles.aiAgentParagraph}>
              Finding relevant academic papers is the first step, not the final goal. After discovering a
              potentially useful source, you may still need to understand its contribution, compare it with
              other studies, or decide how it fits your research question.
            </p>
            <p className={styles.aiAgentParagraph}>
              ResearchPal's AI Research Assistant complements Search Papers by helping you move beyond search
              results. You can discuss your research topic, explore unfamiliar concepts, ask questions about
              academic material, and decide what you should investigate next.
            </p>
            <p className={styles.aiAgentParagraph}>
              Search Papers is designed to help researchers discover scholarly literature using
              natural-language queries, academic filters, paper information and connected citation paths.
              Saved papers can also become part of your Research Library and broader research workflow.
            </p>
          </div>

          <div className={styles.aiAgentExploreCard}>
            <h3 className={styles.aiAgentSubtitle}>
              Use the AI Research Assistant After You Find a Paper
            </h3>
            <p className={styles.aiAgentCardText}>The Agent can help you:</p>
            <ul className={styles.aiAgentList}>
              <li className={styles.aiAgentListItem}>Refine a broad research question</li>
              <li className={styles.aiAgentListItem}>Explore different directions for your research</li>
              <li className={styles.aiAgentListItem}>Understand unfamiliar academic terminology</li>
              <li className={styles.aiAgentListItem}>Discuss findings from papers you discover</li>
              <li className={styles.aiAgentListItem}>Identify questions that deserve further investigation</li>
              <li className={styles.aiAgentListItem}>Connect newly discovered research with your existing work</li>
              <li className={styles.aiAgentListItem}>Move from paper discovery toward analysis and writing</li>
            </ul>
          </div>

          <p className={styles.aiAgentParagraph}>
            This creates a more complete workflow: search for research → understand the evidence →
            investigate further → organize your sources → write.
          </p>
          <p className={styles.aiAgentParagraph}>
            If you want a free AI research assistant that works as part of your academic research workflow,
            try ResearchPal's AI Agent.
          </p>

          <Link to="/ai-agent" className={styles.aiAgentButton}>
            Try the AI Research Assistant
          </Link>
        </div>

        <OtherFeaturesSection currentId="search-papers" />

        {/* FINAL CTA SECTION */}
        <div className={styles.ctaWrapper}>
          <h2 className={styles.ctaTitle}>Start Discovering Research Papers Faster</h2>
          <div className={styles.ctaLabel}>
            Search academic papers, explore citations, and build your research library — all within one intelligent research platform.
          </div>
          <div
            className={styles.ctaButton}
            style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onClick={handleGetStarted}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleGetStarted(); } }}
          >
            <img src={thumbIcon} alt="thumb" className={styles.ctaIcon} />
            Search Research Papers
          </div>
        </div>

      </div>

      <Footer />

      {state.loginModal && (
        <Login open={state.loginModal} onClose={(e) => toggle(e)} />
      )}
      {state.forgotModal && (
        <ForgotPassword
          open={state.forgotModal}
          onClose={() => toggle("forgotModal")}
        />
      )}
      {state.signupModal && (
        <Signup
          open={state.signupModal}
          onClose={(target) => setState((prev) => ({ ...prev, signupModal: false, loginModal: target === "loginModal", forgotModal: target === "forgotModal" }))}
        />
      )}
      {state.contactUsModal && (
        <Contact
          open={state.contactUsModal}
          onClose={() => toggle("contactUsModal")}
        />
      )}
    </Box>
  );
};

export default SearchPapersDetail;
